import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const CATEGORIES = ["Personal", "Fleet Van", "Taxi", "Private Hire", "Executive"];
const EMPTY = { name: "", plate: "", make: "", model: "", year: "", category: "Personal", notes: "", mot_due: "", council_due: "", tax_due: "" };

export default function VehicleForm({ open, onClose, vehicle, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const isNew = !vehicle;

  useEffect(() => {
    if (open) setForm(vehicle ? { ...EMPTY, ...vehicle, year: vehicle.year || "" } : EMPTY);
  }, [open, vehicle]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.plate.trim()) {
      toast.error("Driver/nickname and registration are required");
      return;
    }
    setBusy(true);
    const payload = { ...form, year: form.year ? parseInt(form.year, 10) : null };
    try {
      if (isNew) await api.post("/vehicles", payload);
      else await api.put(`/vehicles/${vehicle.id}`, payload);
      toast.success(isNew ? "Vehicle added to the fleet" : "Vehicle updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-panel border-line text-hi max-w-lg max-h-[90vh] overflow-y-auto" data-testid="vehicle-form-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black uppercase tracking-wide">
            {isNew ? "Add vehicle" : "Edit vehicle"}
          </DialogTitle>
          <DialogDescription className="text-mid text-sm">
            Pin the three deadlines now — you can leave any blank until you know it.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-2 space-y-4" data-testid="vehicle-form">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="vf-name" className="font-mono text-[10px] uppercase tracking-[0.2em] text-mid">Driver / nickname *</Label>
              <Input id="vf-name" data-testid="vehicle-name-input" value={form.name} onChange={set("name")} placeholder="Night Shift" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="vf-plate" className="font-mono text-[10px] uppercase tracking-[0.2em] text-mid">Registration *</Label>
              <Input id="vf-plate" data-testid="vehicle-plate-input" value={form.plate} onChange={set("plate")} placeholder="BX22 WDN" className="uppercase font-mono" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="vf-make" className="font-mono text-[10px] uppercase tracking-[0.2em] text-mid">Make</Label>
              <Input id="vf-make" data-testid="vehicle-make-input" value={form.make} onChange={set("make")} placeholder="Toyota" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="vf-model" className="font-mono text-[10px] uppercase tracking-[0.2em] text-mid">Model</Label>
              <Input id="vf-model" data-testid="vehicle-model-input" value={form.model} onChange={set("model")} placeholder="Prius" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="vf-year" className="font-mono text-[10px] uppercase tracking-[0.2em] text-mid">Year</Label>
              <Input id="vf-year" data-testid="vehicle-year-input" type="number" value={form.year} onChange={set("year")} placeholder="2022" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-mid">Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
              <SelectTrigger data-testid="vehicle-category-select" className="bg-ink border-line">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-panel border-line text-hi">
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="vf-mot" className="font-mono text-[10px] uppercase tracking-[0.2em] text-mid">MOT due</Label>
              <Input id="vf-mot" data-testid="vehicle-mot-date" type="date" value={form.mot_due || ""} onChange={set("mot_due")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="vf-council" className="font-mono text-[10px] uppercase tracking-[0.2em] text-mid">Council test due</Label>
              <Input id="vf-council" data-testid="vehicle-council-date" type="date" value={form.council_due || ""} onChange={set("council_due")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="vf-tax" className="font-mono text-[10px] uppercase tracking-[0.2em] text-mid">Road tax due</Label>
              <Input id="vf-tax" data-testid="vehicle-tax-date" type="date" value={form.tax_due || ""} onChange={set("tax_due")} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="vf-notes" className="font-mono text-[10px] uppercase tracking-[0.2em] text-mid">Notes</Label>
            <Textarea id="vf-notes" data-testid="vehicle-notes-input" value={form.notes} onChange={set("notes")} placeholder="Hackney carriage, keys with Dave…" rows={2} />
          </div>
          <button
            type="submit"
            data-testid="vehicle-save-button"
            disabled={busy}
            className="w-full rounded-full bg-ember py-3 font-display text-lg font-bold uppercase tracking-wider text-ink hover:bg-ember/90 disabled:opacity-40 transition"
          >
            {busy ? "Saving…" : isNew ? "Add to the fleet" : "Save changes"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
